import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RatesFilterStateService } from '../../state/rates-filter-state.service';
import { parseSurrYearList } from '../../state/surr-years';

type FaField = 'rateTerm' | 'surrYears' | 'bonusYears';

/**
 * Phase 11.5 — RailFaChecklistPickerComponent.
 *
 * FA-only numeric multi-select (Rate Term, Surr Years, Bonus Years). These
 * filters are parent-owned (RatesComponent) but proxied to
 * RatesFilterStateService signals (`faRateTerms` / `faSurrYears` /
 * `faBonusYears`) so the rail can read selection reactively and write it.
 * Writes go through the service setter + the existing requestApplyFilters()
 * channel — the FA grid's doesExternalFilterPass reads the same proxied
 * fields, so live-apply works without a new effect.
 *
 * Options are distinct numeric values from the active tab's full dataset
 * (`allData`, FA rows carry `rateTerm` / `surrYears` / `bonusYears`),
 * recomputed when dataVersion() bumps.
 *
 * Field-row chrome (Phase — Task 3b): collapsible `.rail-field` /
 * `.rail-field-head` (name + count/All + ★ pin) with a flush
 * `.rail-field-body`, matching RailCarrierPickerComponent /
 * InlinePickerHostComponent. The picker IS pinnable — `dimKey`
 * (`faRateTerm` / `faSurrYears` / `faBonusYears`) is its pin-store key,
 * emitted via `pinToggle`.
 */
@Component({
  selector: 'app-rail-fa-checklist-picker',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="rail-field" [class.is-open]="open()">
      <div class="rail-field-headrow">
        <button type="button" class="rail-field-head" [attr.aria-expanded]="open()" (click)="toggleRow()">
          <span class="rf-name">{{ label }}</span>
          @if (selected().length > 0) {
            <span class="rf-count">{{ selected().length }}</span>
          } @else {
            <span class="rf-allnone">All</span>
          }
        </button>
        <button type="button" class="rf-star" [class.pinned]="pinned"
                (click)="pinToggle.emit(dimKey)"
                [attr.aria-label]="pinned ? 'Unpin' : 'Pin to top'"
                [title]="pinned ? 'Unpin' : 'Pin to top'">{{ pinned ? '★' : '☆' }}</button>
      </div>
      @if (open()) {
        <div class="rail-field-body">
          <ul class="checklist" role="listbox" aria-multiselectable="true">
            @for (opt of options(); track opt) {
              <li class="option" role="option"
                  [class.on]="isSelected(opt)"
                  [attr.aria-selected]="isSelected(opt)"
                  (click)="toggle(opt)">
                <span class="cb" aria-hidden="true">{{ isSelected(opt) ? '✓' : '' }}</span>
                <span class="label">{{ opt }}</span>
              </li>
            } @empty {
              <li class="empty">No values.</li>
            }
          </ul>
        </div>
      }
    </div>
  `,
  styles: [`
    :host { display: block; }

    /* Field-row chrome — copied from InlinePickerHostComponent so this FA
       row presents pixel-identically to the host-rendered pickers
       (workstream-isolated; copying is correct, do not cross-import). */
    .rail-field-headrow {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .rail-field-head {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 7px 6px 7px 29px;
      background: transparent;
      border: none;
      cursor: pointer;
      font: inherit;
      text-align: left;
      border-radius: 6px;

      &:hover { background: var(--bg); }
    }

    // v4 UX fix: demoted picker-row label
    .rf-name {
      font-size: 12px;
      font-weight: 500;
      color: var(--text2);
    }

    .rf-count {
      margin-left: auto;
      font-family: var(--mono);
      font-size: 10px;
      font-weight: 600;
      color: var(--blue);
      background: var(--blue-soft);
      border: 1px solid var(--blue-border);
      border-radius: 9px;
      padding: 0 6px;
    }

    .rf-allnone {
      margin-left: auto;
      font-size: 11px;
      color: var(--muted);
    }

    .rf-star {
      flex-shrink: 0;
      color: var(--muted-soft);
      font-size: 13px;
      line-height: 1;
      cursor: pointer;
      border: none;
      background: none;
      padding: 4px 6px;
      border-radius: 4px;

      &:hover:not(:disabled) { background: var(--bg); color: var(--navy); }
      &.pinned { color: var(--navy); }
      &:disabled { opacity: 0.4; cursor: not-allowed; }
    }

    .rail-field-body {
      padding: 2px 2px 8px 8px;
      max-height: 50vh;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    .checklist {
      list-style: none; margin: 0; padding: 4px 0;
      overflow-y: auto; flex: 1; min-height: 0; max-height: 320px;
    }
    .option {
      display: flex; align-items: center; gap: 8px;
      padding: 6px 14px; font-size: 12px; color: var(--text2);
      cursor: pointer; user-select: none;
      &:hover { background: var(--bg); color: var(--navy); }
      &.on { color: var(--navy); font-weight: 600; }
      .cb {
        width: 14px; height: 14px; border-radius: 2px;
        border: 1px solid var(--border); display: grid; place-items: center;
        font-size: 9px; color: #fff; flex-shrink: 0; background: var(--panel);
      }
      &.on .cb { background: var(--navy); border-color: var(--navy); }
      .label { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    }
    .empty { padding: 12px 14px; font-size: 11.5px; color: var(--muted); font-style: italic; }
  `],
})
export class RailFaChecklistPickerComponent {
  @Input({ required: true }) field!: FaField;
  @Input() pinned = false;
  @Output() pinToggle = new EventEmitter<string>();
  @Output() closeRequest = new EventEmitter<string>();

  /** Canonical DimKey for this FA picker (pin store key). */
  protected get dimKey(): string {
    if (this.field === 'rateTerm') return 'faRateTerm';
    if (this.field === 'surrYears') return 'faSurrYears';
    return 'faBonusYears';
  }

  private readonly filterState = inject(RatesFilterStateService);

  /** Collapse state — mirrors RailCarrierPickerComponent. Auto-expand when a
   *  value is selected, unless the user has explicitly toggled. */
  private readonly _userToggled = signal(false);
  private readonly _expanded = signal(false);

  constructor() {
    // Collapse this field when its Active-Filters chip is cleared (clearDim →
    // dimClearedRequest). dimKey ('faRateTerm'/'faSurrYears'/'faBonusYears')
    // matches the clearDim dim exactly. In-picker unchecking doesn't bump the
    // signal, so it won't collapse here.
    effect(() => {
      const req = this.filterState.dimClearedRequest();
      untracked(() => {
        if (req.tick === 0) return;
        if (req.dim === this.dimKey) {
          this._userToggled.set(true);
          this._expanded.set(false);
        }
      });
    });
  }

  protected open(): boolean {
    if (this._userToggled()) return this._expanded();
    return this.selected().length > 0;
  }

  toggleRow(): void {
    // Capture the currently-visible state BEFORE marking user-toggled, so the
    // first click on an auto-expanded field collapses it (rather than no-op).
    const wasOpen = this.open();
    this._userToggled.set(true);
    this._expanded.set(!wasOpen);
  }

  protected get label(): string {
    if (this.field === 'rateTerm') return 'Rate Term';
    if (this.field === 'surrYears') return 'Surrender Years';
    return 'Bonus Years';
  }
  protected get unitSuffix(): string {
    return ' yr';
  }

  /** Distinct numeric values from the active tab's rows, SCOPED to the
   *  current Carriers selection (selected companies + their selected
   *  products) — so Rate Term / Surr Years / Bonus Years only offer values
   *  that exist under the picked carriers; the full tab universe when nothing
   *  is picked. Reads companyFilterRequest() so it re-runs on carrier change. */
  protected readonly options = computed<number[]>(() => {
    this.filterState.dataVersion();
    this.filterState.companyFilterRequest();
    const tab = this.filterState.activeTab();
    const cf = this.filterState.getCompanyFilterForTab(tab);
    const scoped = !!cf && (cf.companies.length > 0 || cf.products.length > 0);
    const companies = scoped ? new Set(cf!.companies) : null;
    const products = scoped && cf!.products.length > 0 ? new Set(cf!.products) : null;
    const rowField = this.field;
    const set = new Set<number>();
    for (const row of this.filterState.allData) {
      const r = row as Record<string, unknown>;
      if (companies) {
        const co = String(r['company'] ?? r['companyName'] ?? '');
        if (!companies.has(co)) continue;
        if (products) {
          const pr = String(r['product'] ?? r['productName'] ?? '');
          if (pr && !products.has(pr)) continue;
        }
      }
      const v = r[rowField];
      if (v == null || v === '') continue;
      if (rowField === 'surrYears') {
        // A multi-term product arrives as a CSV ("5, 7"), which Number() turns
        // into NaN — those rows used to contribute no options at all, so their
        // years were unpickable. Shared with the grid filter's own parse so the
        // list offered here can't drift from what the filter matches.
        for (const y of parseSurrYearList(v)) set.add(y);
        continue;
      }
      const n = Number(v);
      if (Number.isFinite(n)) set.add(n);
    }
    return Array.from(set).sort((a, b) => a - b);
  });

  protected readonly selected = computed<number[]>(() => {
    if (this.field === 'rateTerm') return this.filterState.faRateTerms();
    if (this.field === 'surrYears') return this.filterState.faSurrYears();
    return this.filterState.faBonusYears();
  });

  isSelected(opt: number): boolean {
    return this.selected().includes(opt);
  }

  toggle(opt: number): void {
    const cur = this.selected();
    const next = cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt];
    this.write(next);
  }

  clearAll(): void {
    this.write([]);
  }

  private write(next: number[]): void {
    if (this.field === 'rateTerm') this.filterState.setFaRateTerms(next);
    else if (this.field === 'surrYears') this.filterState.setFaSurrYears(next);
    else this.filterState.setFaBonusYears(next);
    this.filterState.requestApplyFilters();
  }
}
